import React, { useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { ImagePicker } from "@/components/ImagePicker";
import { RichEditor } from "@/components/RichEditor";
import { imgSrc } from "@/lib/api";
import { formatDate } from "@/lib/format";

const inputCls = "w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]";
const Field = ({ label, hint, children }) => (<div><label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">{label}{hint && <span className="ml-1 font-normal opacity-70">· {hint}</span>}</label>{children}</div>);
const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const EMPTY = { title: "", slug: "", excerpt: "", author: "Archedia Advisory", category: "", tags: "", coverImage: "", content: "", metaTitle: "", metaDescription: "", status: "draft", publishedAt: new Date().toISOString().slice(0, 10) };

const Sheet = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:p-8" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className={`my-4 w-full ${wide ? "max-w-5xl" : "max-w-3xl"} rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-2xl`}>
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-6 py-4"><h3 className="font-serif text-xl text-[var(--text-primary)]">{title}</h3><button onClick={onClose} className="rounded-full p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-primary)]"><X className="h-5 w-5" /></button></div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Preview = ({ post, onClose }) => (
  <Sheet title="Preview" onClose={onClose} wide>
    <article data-testid="blog-preview">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[var(--accent-gold)]">{post.category || "Uncategorised"}</div>
      <h1 className="mt-2 font-serif text-4xl font-light text-[var(--text-primary)]">{post.title || "Untitled"}</h1>
      {post.excerpt && <p className="mt-3 text-lg text-[var(--text-muted)]">{post.excerpt}</p>}
      <div className="mt-3 text-xs text-[var(--text-muted)]">{post.author} · {formatDate(post.publishedAt)}</div>
      {post.coverImage && <img src={imgSrc(post.coverImage)} alt="" className="mt-6 max-h-[60vh] w-full rounded-2xl object-contain" />}
      <div className="prose-arch mt-8" dangerouslySetInnerHTML={{ __html: post.content || "<p><em>No content yet.</em></p>" }} />
    </article>
  </Sheet>
);

export const AdminBlogs = () => {
  const { data, addItem, updateItem, removeItem } = useApp();
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(false);
  const blogs = data.blogs || [];
  const cats = data.blogCategories || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => setForm({ ...EMPTY, category: cats[0]?.name || "" });
  const openEdit = (b) => setForm({ ...b, tags: (b.tags || []).join(", "), publishedAt: (b.publishedAt || b.createdAt || "").slice(0, 10) });

  const save = async (statusOverride) => {
    const f = form;
    if (!f.title.trim()) { toast.error("Title is required."); return; }
    if (!f.category) { toast.error("Pick a category."); return; }
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(), slug: slugify(f.slug || f.title), excerpt: f.excerpt || "", author: f.author || "Archedia", category: f.category,
        tags: String(f.tags || "").split(",").map((t) => t.trim()).filter(Boolean), coverImage: f.coverImage || "", content: f.content || "",
        metaTitle: f.metaTitle || f.title.trim(), metaDescription: f.metaDescription || f.excerpt || "", status: statusOverride || f.status || "draft",
        publishedAt: f.publishedAt ? new Date(f.publishedAt).toISOString() : new Date().toISOString(),
      };
      if (f.id) await updateItem("blogs", f.id, payload); else await addItem("blogs", payload);
      toast.success(payload.status === "published" ? "Article published — live on the website." : "Draft saved.");
      setForm(null);
    } catch (e) { toast.error("Could not save article."); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (b) => {
    const status = b.status === "published" ? "draft" : "published";
    await updateItem("blogs", b.id, { status });
    toast.success(status === "published" ? "Published" : "Unpublished");
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    if (cats.some((c) => c.name.toLowerCase() === name.toLowerCase())) { toast.error("Category already exists."); return; }
    await addItem("blogCategories", { name, slug: slugify(name) });
    setNewCat(""); toast.success("Category added");
  };

  const deleteCategory = async (c) => {
    if (blogs.some((b) => b.category === c.name)) { toast.error("Move or delete articles in this category first."); return; }
    if (window.confirm(`Delete category "${c.name}"?`)) { await removeItem("blogCategories", c.id); toast.success("Category removed"); }
  };

  return (
    <div data-testid="admin-blogs">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-[var(--text-muted)]">{blogs.length} articles · {blogs.filter((b) => b.status === "published").length} published</span>
        <button onClick={openNew} data-testid="add-blog-btn" className="btn-gold !px-4 !py-2 text-xs"><Plus className="h-4 w-4" /> New Article</button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="space-y-3">
          {blogs.length === 0 && <div className="arch-card p-8 text-center text-sm text-[var(--text-muted)]">No articles yet. Create your first one.</div>}
          {blogs.map((b) => (
            <div key={b.id} className="arch-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid={`admin-blog-${b.id}`}>
              <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg bg-[var(--accent-a10)]">{b.coverImage && <img src={imgSrc(b.coverImage)} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${b.status === "published" ? "bg-[var(--accent-a15)] text-[var(--accent-gold)]" : "bg-[var(--bg-primary)] text-[var(--text-muted)]"}`} data-testid={`blog-status-${b.id}`}>{b.status}</span><span className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{b.category}</span></div>
                <div className="mt-1 truncate font-serif text-lg text-[var(--text-primary)]">{b.title}</div>
                <div className="truncate text-xs text-[var(--text-muted)]">/blog/{b.slug} · {b.author} · {formatDate(b.publishedAt || b.createdAt)}</div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => setPreview({ ...b })} title="Preview" data-testid={`preview-blog-${b.id}`} className="rounded-md p-1.5 text-[var(--text-muted)] hover:text-[var(--accent-gold)]"><ExternalLink className="h-4 w-4" /></button>
                <button onClick={() => openEdit(b)} title="Edit" data-testid={`edit-blog-${b.id}`} className="rounded-md p-1.5 text-[var(--text-muted)] hover:text-[var(--accent-gold)]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => toggleStatus(b)} title={b.status === "published" ? "Unpublish" : "Publish"} data-testid={`toggle-blog-${b.id}`} className="rounded-md p-1.5 text-[var(--text-muted)] hover:text-[var(--accent-gold)]">{b.status === "published" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                <button onClick={() => { if (window.confirm(`Delete "${b.title}"?`)) { removeItem("blogs", b.id); toast.success("Article deleted"); } }} title="Delete" data-testid={`delete-blog-${b.id}`} className="rounded-md p-1.5 text-[var(--text-muted)] hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="arch-card h-fit p-5" data-testid="blog-categories-panel">
          <div className="mb-3 text-sm font-medium text-[var(--text-primary)]">Categories</div>
          <ul className="space-y-2">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm" data-testid={`blog-category-${c.id}`}>
                <span className="text-[var(--text-primary)]">{c.name} <span className="text-xs text-[var(--text-muted)]">({blogs.filter((b) => b.category === c.name).length})</span></span>
                <button onClick={() => deleteCategory(c)} data-testid={`delete-category-${c.id}`} className="rounded p-1 text-[var(--text-muted)] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder="New category" data-testid="new-category-input" className={inputCls} />
            <button onClick={addCategory} data-testid="add-category-btn" className="btn-outline-gold !px-3 !py-2 text-xs"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {form && (
        <Sheet title={form.id ? "Edit Article" : "New Article"} onClose={() => setForm(null)} wide>
          <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
            <div className="space-y-4">
              <Field label="Title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.id ? form.slug : slugify(e.target.value) })} data-testid="blog-title-input" className={inputCls} /></Field>
              <Field label="URL slug" hint="/blog/…"><input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} data-testid="blog-slug-input" className={inputCls} /></Field>
              <Field label="Excerpt"><textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-excerpt-input" className={inputCls} /></Field>
              <Field label="Content"><RichEditor value={form.content} onChange={(html) => set("content", html)} /></Field>
            </div>
            <div className="space-y-4">
              <ImagePicker single value={form.coverImage} onChange={(coverImage) => set("coverImage", coverImage)} label="Cover Image" testId="blog-cover" />
              <Field label="Category">
                <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="blog-category-select" className={inputCls}>
                  <option value="">Select…</option>
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Tags" hint="comma separated"><input value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="blog-tags-input" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Author"><input value={form.author} onChange={(e) => set("author", e.target.value)} data-testid="blog-author-input" className={inputCls} /></Field>
                <Field label="Date"><input type="date" value={form.publishedAt} onChange={(e) => set("publishedAt", e.target.value)} data-testid="blog-date-input" className={inputCls} /></Field>
              </div>
              <div className="rounded-lg border border-[var(--border-subtle)] p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">SEO</div>
                <Field label="Meta title" hint={`${(form.metaTitle || "").length}/60`}><input value={form.metaTitle} onChange={(e) => set("metaTitle", e.target.value)} placeholder={form.title} data-testid="blog-meta-title" className={inputCls} /></Field>
                <div className="mt-2"><Field label="Meta description" hint={`${(form.metaDescription || "").length}/160`}><textarea rows={3} value={form.metaDescription} onChange={(e) => set("metaDescription", e.target.value)} placeholder={form.excerpt} data-testid="blog-meta-desc" className={inputCls} /></Field></div>
              </div>
              <Field label="Status">
                <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="blog-status-select" className={inputCls}><option value="draft">Draft</option><option value="published">Published</option></select>
              </Field>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
            <button onClick={() => setPreview({ ...form, tags: String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="blog-preview-btn" className="btn-outline-gold !py-2 text-xs"><ExternalLink className="h-4 w-4" /> Preview</button>
            <button onClick={() => save("draft")} disabled={saving} data-testid="blog-save-draft" className="btn-outline-gold !py-2 text-xs disabled:opacity-60">Save Draft</button>
            <button onClick={() => save("published")} disabled={saving} data-testid="blog-publish" className="btn-gold !py-2 text-xs disabled:opacity-60">{saving ? "Saving…" : form.status === "published" && form.id ? "Update & Publish" : "Publish"}</button>
          </div>
        </Sheet>
      )}
      {preview && <Preview post={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export default AdminBlogs;
